<script setup lang="ts">
import { computed, type Component, type CSSProperties, type PropType } from 'vue';
import { IconEye, IconFlash, IconTools } from '@/components/design/icons';
import { ChatConfirmationMode, type LlmProvider } from '@/api';
import { CONFIRMATION_MODE_OPTIONS, useConfirmationMode } from '@/composables/useConfirmationMode';
import {
  formatModelLabel,
  formatModelProviderLabel,
  getModelOptionKey,
  useModelSelection,
} from '@/composables/useModelSelection';
import GaiaDivider from '../../base/GaiaDivider.vue';
import GaiaPopover from '../../base/GaiaPopover.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaChip from '../../base/GaiaChip.vue';
import GaiaPickerOption from '../../composite/GaiaPickerOption.vue';
import {
  gaiaAccent,
  gaiaAmber,
  gaiaControlHeight,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaShadow,
} from '../../themes/tokens';

const props = defineProps({
  /** Where the panel opens relative to the pill — below on home, above in a chat. */
  placement: {
    type: String as PropType<'bottom-start' | 'top-start'>,
    default: 'bottom-start',
  },
  /**
   * Narrow host: the pill keeps the mode and drops the model. The mode is the
   * one that changes what a send does, so it is the half worth the width; the
   * model is still a click away in the panel.
   */
  dense: {
    type: Boolean,
    default: false,
  },
  /**
   * The model the open thread was created with. A thread is pinned to its model
   * for life, so while one is open the panel shows this and the picker is
   * read-only. Null on a new chat, where the stored preference is picked.
   */
  pinnedModel: {
    type: Object as PropType<{ provider: LlmProvider; model: string } | null>,
    default: null,
  },
});

/**
 * Each mode owns a tint: the pill, and the segment that selects it, are drawn
 * in it. READ — shown as "Manual" — is the supervised default (violet, the
 * app's accent), Auto is the unattended one (amber) — a chosen setting, not a
 * warning, hence its own palette rather than gaiaStatus.
 */
const MODE_TINT: Record<
  ChatConfirmationMode,
  { ink: string; surface: string; surfaceOpen: string; border: string; ring: string }
> = {
  [ChatConfirmationMode.READ]: {
    ink: gaiaAccent.strong,
    surface: gaiaAccent.surface,
    surfaceOpen: gaiaAccent.halo,
    border: gaiaAccent.border,
    ring: gaiaAccent.ring,
  },
  [ChatConfirmationMode.AUTO]: {
    ink: gaiaAmber.strong,
    surface: gaiaAmber.surface,
    surfaceOpen: gaiaAmber.surfaceOpen,
    border: gaiaAmber.border,
    ring: gaiaAmber.ring,
  },
};

const MODE_ICON: Record<ChatConfirmationMode, Component> = {
  [ChatConfirmationMode.READ]: IconEye,
  [ChatConfirmationMode.AUTO]: IconFlash,
};

const {
  currentConfirmationMode,
  confirmationModeLabel,
  confirmationModeDescription,
  selectConfirmationMode,
} = useConfirmationMode();

const {
  showModelMenu,
  modelsLoading,
  modelMenuError,
  availableChatModels,
  selectedModelProviderLabel,
  currentChatModelSelection,
  chatModelProviderGroups,
  filteredChatModels,
  isCurrentChatModel,
  selectModelProviderGroup,
  handleSelectChatModel,
} = useModelSelection();

/** The pinned model while a thread is open, the picked one otherwise. */
const shownModel = computed(() => props.pinnedModel ?? currentChatModelSelection.value);

const modelLabel = computed(() => {
  const selection = shownModel.value;
  return selection ? formatModelLabel(selection.model, selection.provider) : 'Select model';
});

/** "Google (EU) / Gemini 3.5 Flash" — the panel header's right-hand readout. */
const modelSummary = computed(() => {
  const selection = shownModel.value;
  if (!selection) return 'Current model unavailable';
  return `${formatModelProviderLabel(selection.provider, selection.model)} / ${formatModelLabel(
    selection.model,
    selection.provider
  )}`;
});

const tint = computed(() => MODE_TINT[currentConfirmationMode.value]);

// The pill deepens and its border goes to the mode ink while the panel is open.
const pillInk = computed(() => tint.value.ink);
const pillSurface = computed(() =>
  showModelMenu.value ? tint.value.surfaceOpen : tint.value.surface
);
const pillBorder = computed(() => (showModelMenu.value ? tint.value.ink : tint.value.border));

/**
 * The active segment is white with a ring in its own mode's tint, so the colors
 * vary per row and can't come from a `v-bind()` rule.
 */
function segmentStyle(mode: ChatConfirmationMode): CSSProperties {
  if (mode !== currentConfirmationMode.value) {
    return { color: gaiaNeutral.textSubtle, borderColor: 'transparent' };
  }
  const modeTint = MODE_TINT[mode];
  return {
    color: modeTint.ink,
    background: gaiaNeutral.surface,
    borderColor: modeTint.ink,
    boxShadow: `0 0 0 2px ${modeTint.ring}, ${gaiaShadow.card}`,
  };
}

/** Provider chips are 28px rather than the chip default, and go ink when active. */
function providerChipOverrides(active: boolean): Record<string, string> {
  return {
    heightMedium: '28px',
    paddingMedium: '0 10px',
    fontSizeMedium: '12px',
    ...(active
      ? {
          color: gaiaNeutral.surfaceTint,
          colorHover: gaiaNeutral.surfaceTint,
          colorPressed: gaiaNeutral.surfaceTint,
          colorFocus: gaiaNeutral.surfaceTint,
          border: `1px solid ${gaiaNeutral.ink}`,
          textColor: gaiaNeutral.ink,
          fontWeight: String(gaiaFontWeight.semibold),
        }
      : {}),
  };
}

// The pill sits in the component's own tree, so its chrome can come from
// `v-bind()`.
const ink = gaiaNeutral.ink;
const pillHeight = gaiaControlHeight.composerControl;

/**
 * The panel cannot. `v-bind()` in scoped CSS declares its variables on the
 * component's root element, and the popover teleports its content to <body> —
 * out of that tree, so nothing inherits them and every color resolves to
 * nothing (a see-through panel). The panel therefore declares the variables on
 * itself and the rules below read them, which also reaches its descendants.
 */
const panelVars = {
  '--gaia-panel-surface': gaiaNeutral.surface,
  '--gaia-panel-border': gaiaNeutral.borderCard,
  '--gaia-panel-shadow': gaiaShadow.panel,
  '--gaia-panel-track': gaiaNeutral.surfaceTint,
  '--gaia-panel-row-border': gaiaNeutral.borderSoft,
} as CSSProperties;
</script>

<template>
  <gaia-popover v-model:show="showModelMenu" :placement="props.placement">
    <template #trigger>
      <!-- Mode-tinted pill: the tint is per-mode and carries an open state, so
           it can't come from the global Button theme. -->
      <button
        type="button"
        class="pill"
        data-tour="chat.model-button"
        aria-label="Chat settings: mode and model"
        :aria-expanded="showModelMenu"
      >
        <n-icon :component="IconTools" :size="14" />
        <gaia-text size="dense" weight="semibold">{{ confirmationModeLabel }}</gaia-text>
        <template v-if="!props.dense">
          <gaia-text size="dense" tone="muted">·</gaia-text>
          <gaia-text truncate size="dense" tone="tertiary">{{ modelLabel }}</gaia-text>
        </template>
      </button>
    </template>

    <div class="panel" data-tour="chat.model-menu" :style="panelVars">
      <n-flex vertical :size="9" class="section">
        <gaia-text tag="div" size="dense" weight="bold">Confirmation mode</gaia-text>
        <n-flex
          :size="4"
          :wrap="false"
          role="radiogroup"
          aria-label="Confirmation mode"
          :style="{ padding: '3px', borderRadius: '10px', background: 'var(--gaia-panel-track)' }"
        >
          <button
            v-for="option in CONFIRMATION_MODE_OPTIONS"
            :key="option.mode"
            type="button"
            class="segment"
            role="radio"
            :aria-checked="option.mode === currentConfirmationMode"
            :style="segmentStyle(option.mode)"
            @click="selectConfirmationMode(option.mode)"
          >
            <n-icon :component="MODE_ICON[option.mode]" :size="14" />
            <gaia-text
              size="dense"
              :weight="option.mode === currentConfirmationMode ? 'bold' : 'medium'"
            >
              {{ option.label }}
            </gaia-text>
          </button>
        </n-flex>
        <gaia-text tag="div" size="meta" tone="subtle">
          {{ confirmationModeDescription }}
        </gaia-text>
      </n-flex>

      <gaia-divider variant="subtle" :style="{ margin: '0' }" />

      <n-flex vertical :size="10" class="section">
        <n-flex align="baseline" :size="8" :wrap="false">
          <gaia-text size="dense" weight="bold">Chat model</gaia-text>
          <gaia-text truncate size="meta" tone="subtle" :style="{ marginLeft: 'auto' }">{{
            modelSummary
          }}</gaia-text>
        </n-flex>

        <!-- Two even columns rather than a wrapping row: provider names differ
             in length, so a flex row gave every chip a different width. The
             grid fixes the width, `block` makes the chip fill its cell, and the
             count is pinned right so the labels all start on the same edge. -->
        <n-grid
          v-if="!props.pinnedModel && chatModelProviderGroups.length"
          :cols="2"
          :x-gap="6"
          :y-gap="6"
        >
          <n-gi v-for="group in chatModelProviderGroups" :key="group.provider">
            <gaia-chip
              block
              :theme-overrides="
                providerChipOverrides(group.provider === selectedModelProviderLabel)
              "
              @click="selectModelProviderGroup(group.provider)"
            >
              <n-flex
                align="center"
                justify="space-between"
                :wrap="false"
                :style="{ width: '100%' }"
              >
                <gaia-text truncate>{{ group.label }}</gaia-text>
                <gaia-text
                  size="control"
                  :tone="group.provider === selectedModelProviderLabel ? 'tertiary' : 'muted'"
                  :style="{ fontVariantNumeric: 'tabular-nums' }"
                >
                  {{ group.models.length }}
                </gaia-text>
              </n-flex>
            </gaia-chip>
          </n-gi>
        </n-grid>
      </n-flex>

      <!-- An open thread keeps the model it started with, so the picker gives
           way to the reason it is gone. -->
      <div v-if="props.pinnedModel" class="section state">
        <gaia-text size="dense" tone="subtle">
          Pinned to this conversation. Start a new chat to use a different model.
        </gaia-text>
      </div>

      <n-flex v-else-if="modelsLoading" align="center" :size="8" class="section state">
        <n-spin :size="14" />
        <gaia-text size="dense" tone="subtle">Loading models…</gaia-text>
      </n-flex>

      <div v-else-if="modelMenuError" class="section state">
        <n-alert type="error" :show-icon="false">
          <gaia-text size="dense">{{ modelMenuError }}</gaia-text>
        </n-alert>
      </div>

      <div v-else-if="!availableChatModels.length" class="section state">
        <gaia-text size="dense" tone="subtle">No models available</gaia-text>
      </div>

      <div
        v-else
        :style="{
          maxHeight: '176px',
          overflowY: 'auto',
          borderTop: '1px solid var(--gaia-panel-row-border)',
        }"
      >
        <gaia-picker-option
          v-for="modelOption in filteredChatModels"
          :key="getModelOptionKey(modelOption)"
          :label="formatModelLabel(modelOption.model, modelOption.provider)"
          :meta="formatModelProviderLabel(modelOption.provider, modelOption.model)"
          :selected="isCurrentChatModel(modelOption)"
          @select="handleSelectChatModel(modelOption)"
        />
      </div>
    </div>
  </gaia-popover>
</template>

<style scoped>
/* The pill and the floating panel are both bespoke surfaces from mock 5a: a
   per-mode tint and a composed ring shadow, neither of which naive's Button or
   Popover themes can express. Values come from the shared tokens. */
.pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
  /* Height from the shared control token rather than from padding: the attach
     trigger next to it is a themed n-button whose height is a theme var, so
     both sides have to read the same number to line up. */
  height: v-bind(pillHeight);
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid v-bind(pillBorder);
  background: v-bind(pillSurface);
  color: v-bind(pillInk);
  font-family: inherit;
  cursor: pointer;
}

/* Hover leaves the mode tint for plain ink, so the pill reads as a control
   under the pointer rather than as a stronger version of its own state. */
.pill:hover {
  border-color: v-bind(ink);
  color: v-bind(ink);
}

.panel {
  width: 352px;
  background: var(--gaia-panel-surface);
  border: 1px solid var(--gaia-panel-border);
  border-radius: 14px;
  box-shadow: var(--gaia-panel-shadow);
  overflow: hidden;
}

.section {
  padding: 14px 16px;
}

.state {
  padding-top: 0;
}

.segment {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  font-family: inherit;
  cursor: pointer;
}
</style>
